import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

import { DEFAULT_FILL, ProgressRowTone, TONE_FILLS } from '../ProgressRow';
import { formatSharePercent, sharePercent } from '../formatMetric';
import { visuallyHidden } from '../visuallyHidden';

/* NOTE (conventions/repo-guidelines/patterns.md § When deviating, document why): this is `ProgressRow`
   drawn against N populations rather than one. `ProgressRow` puts a single reading on the label's own
   line and the track underneath at full width; `4182-9637` puts the label alone on the first line and
   the track plus TWO value columns on the second, under the column headings `No Prod. Holding` and
   `Prod. Holding`. Those are different geometries, not a prop apart, so the bar's fill mapping is
   imported from `ProgressRow` instead of being copied and the rest is drawn here. A real `<table>`
   rather than stacked divs: the two columns are populations, and a screen reader has to be able to
   say which population a reading belongs to. PLA-8815. */

export interface ISignalMatrixColumn {
  key: string;
  heading: string;
}

export interface ISignalMatrixRow {
  label: string;
  /**
   * Level as a 0-1 fraction for the row's own bar, on the family's share scale. Omitted — or
   * non-finite — draws no track at all: an empty one asserts a measured zero.
   */
  pct?: number;
  tone?: ProgressRowTone;
  /** Pre-formatted per column key. A column the row does not apply to states so in its own words. */
  values: Record<string, string>;
}

export interface ISignalMatrixProps {
  /** The populations the readings are broken down by, left to right. */
  columns: ISignalMatrixColumn[];
  rows: ISignalMatrixRow[];
  /** Names the whole matrix to assistive tech; the visible heading sits outside the table. */
  caption: string;
  className?: string;
}

/* Read off `4182-9637`: the signals panel is ~405px wide and its bar stops at ~245px, leaving the two
   reading columns ~80px each. Fixed rather than proportional so the two columns line up with their
   headings and with each other down the whole list — a percentage width would let a wider reading in
   one row shift that row's neighbour out of the column.
   80px was too narrow for this repo's own type: the `No Prod. Holding` heading needs ~96px and a
   value like `Not Applicable` needs ~84px at the shipped font, so both overflowed their `nowrap` cell
   into the next column with no gap between them. Widened to fit the actual rendered content instead
   of the value measured off the node. */
const VALUE_COLUMN_WIDTH = '100px';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const HeadCell = styled.th`
  padding: 0 0 ${tokens.space['8']} ${tokens.space['8']};
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
  text-align: left;
  vertical-align: bottom;
  white-space: nowrap;
`;

// The bar column takes whatever the two fixed reading columns leave.
const SignalHeadCell = styled(HeadCell)`
  width: auto;
  padding-left: 0;

  > span {
    ${visuallyHidden}
  }
`;

const ValueHeadCell = styled(HeadCell)`
  width: ${VALUE_COLUMN_WIDTH};
`;

const BodyRow = styled.tr`
  > * {
    padding-top: ${tokens.space['12']};
  }

  &:first-child > * {
    padding-top: 0;
  }
`;

const SignalCell = styled.th`
  padding-left: 0;
  text-align: left;
  vertical-align: bottom;
  font-weight: ${tokens.type['body-weight']};
`;

const RowLabel = styled.span`
  display: block;
  margin-bottom: ${tokens.space['8']};
  overflow: hidden;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Same 6px pill, radius and unfilled ink as `ProgressRow`'s track: the two rows are the same readout
// in two layouts and must not differ in weight.
const Track = styled.div`
  position: relative;
  overflow: hidden;
  height: 6px;
  border-radius: ${tokens.radius.xs};
  background: ${tokens.color['track-bg']};
`;

const Fill = styled.span`
  display: block;
  height: 100%;
  border-radius: inherit;
`;

const ValueCell = styled.td`
  width: ${VALUE_COLUMN_WIDTH};
  padding-left: ${tokens.space['8']};
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
  vertical-align: bottom;
  white-space: nowrap;
`;

const SignalMatrix: React.FC<ISignalMatrixProps> = ({ columns, rows, caption, className }) => {
  const matrixRows = Array.isArray(rows) ? rows : [];
  const matrixColumns = Array.isArray(columns) ? columns : [];

  if (matrixRows.length === 0 || matrixColumns.length === 0) {
    return null;
  }

  return (
    <Table className={className} aria-label={caption} data-testid="signal-matrix">
      <thead>
        <tr>
          <SignalHeadCell scope="col">
            <span>{caption}</span>
          </SignalHeadCell>
          {matrixColumns.map(column => (
            <ValueHeadCell key={column.key} scope="col" data-testid="signal-matrix-heading">
              {column.heading}
            </ValueHeadCell>
          ))}
        </tr>
      </thead>
      <tbody>
        {matrixRows.map(row => {
          const hasLevel = Number.isFinite(row.pct);
          const fill = row.tone ? TONE_FILLS[row.tone] : DEFAULT_FILL;

          return (
            <BodyRow key={row.label} data-testid="signal-matrix-row" data-tone={row.tone || 'none'}>
              <SignalCell scope="row">
                <RowLabel data-testid="signal-matrix-label" title={row.label}>
                  {row.label}
                </RowLabel>
                {/* An unmeasured level draws no track at all: an empty one asserts a measured zero. */}
                {hasLevel ? (
                  <Track
                    role="img"
                    aria-label={formatSharePercent(row.pct)}
                    data-testid="signal-matrix-track"
                  >
                    <Fill
                      data-color={fill}
                      data-testid="signal-matrix-fill"
                      style={{ width: `${sharePercent(row.pct)}%`, background: fill }}
                    />
                  </Track>
                ) : null}
              </SignalCell>
              {matrixColumns.map(column => (
                <ValueCell key={column.key} data-testid="signal-matrix-value">
                  {row.values[column.key]}
                </ValueCell>
              ))}
            </BodyRow>
          );
        })}
      </tbody>
    </Table>
  );
};

export default SignalMatrix;
